import PropTypes from 'prop-types';
import { BsCheck, BsPencil, BsTrash } from 'react-icons/bs';

import Tag from '#globalComponents/Tag/Tag';

/**
 * ToneCard - Display card for a tone of voice option
 * Shows name, description, and recommended industries as tags
 */
const ToneCard = ({ tone, selected = false, onSelect, onEdit, onDelete, disabled = false }) => {
	const handleClick = () => {
		if (!disabled && onSelect) {
			// Toggle selection: if already selected, deselect (pass null)
			onSelect(selected ? null : tone.id);
		}
	};

	const handleEdit = (e) => {
		e.stopPropagation();
		if (onEdit) onEdit(tone);
	};

	const handleDelete = (e) => {
		e.stopPropagation();
		if (onDelete) onDelete(tone.id);
	};

	return (
		<div
			onClick={handleClick}
			className={`
				relative p-4 border-2 rounded-card transition-all cursor-pointer
				${selected ? 'border-primary-500 bg-primary-100' : 'border-bgBase-500 hover:border-bgBase-600 bg-white'}
				${disabled ? 'opacity-50 cursor-not-allowed' : ''}
			`}
		>
			{/* Top right actions area */}
			<div className="absolute top-2 right-2 flex items-center gap-1">
				{/* Custom tone actions */}
				{!tone.isSystem && (onEdit || onDelete) && (
					<>
						{onEdit && (
							<button
								type="button"
								onClick={handleEdit}
								className="p-1 text-bgBase-800 hover:text-text hover:bg-bgBase-400 rounded"
								title="Bearbeiten"
							>
								<BsPencil className="w-4 h-4" />
							</button>
						)}
						{onDelete && (
							<button
								type="button"
								onClick={handleDelete}
								className="p-1 text-bgBase-800 hover:text-warning-700 hover:bg-warning-200 rounded"
								title="Löschen"
							>
								<BsTrash className="w-4 h-4" />
							</button>
						)}
					</>
				)}

				{/* Selection indicator */}
				{selected && (
					<div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
						<BsCheck className="w-3 h-3 text-white" />
					</div>
				)}
			</div>

			<div className={`${!tone.isSystem && (onEdit || onDelete) ? 'pr-16' : selected ? 'pr-8' : ''}`}>
				<div className="font-medium text-text">{tone.name}</div>
				<div className="text-sm text-text mt-1 line-clamp-2">{tone.shortDescription}</div>

				{/* Word count badge */}
				{tone.targetWordCount && (
					<div className="text-xs text-bgBase-800 mt-2">
						{tone.targetWordCount.min}-{tone.targetWordCount.max} Wörter
					</div>
				)}

				{/* Recommended for tags */}
				{tone.recommendedFor && tone.recommendedFor.length > 0 && (
					<div className="flex flex-wrap gap-1 mt-2">
						{tone.recommendedFor.slice(0, 3).map((tag) => (
							<Tag key={tag} label={tag} tone="neutral" />
						))}
						{tone.recommendedFor.length > 3 && (
							<span className="relative group text-xs text-bgBase-800 cursor-default">
								+{tone.recommendedFor.length - 3}
								{/* Tooltip with remaining industries */}
								<div className="absolute left-0 bottom-full mb-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
									<div className="bg-text text-white text-xs rounded shadow-elevated p-2 whitespace-nowrap">
										{tone.recommendedFor.slice(3).join(', ')}
									</div>
								</div>
							</span>
						)}
					</div>
				)}

				{/* System badge */}
				{tone.isSystem && (
					<div className="mt-2">
						<span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded">System</span>
					</div>
				)}
			</div>
		</div>
	);
};

ToneCard.propTypes = {
	tone: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		shortDescription: PropTypes.string.isRequired,
		targetWordCount: PropTypes.shape({
			min: PropTypes.number,
			max: PropTypes.number,
		}),
		recommendedFor: PropTypes.arrayOf(PropTypes.string),
		isSystem: PropTypes.bool,
	}).isRequired,
	selected: PropTypes.bool,
	onSelect: PropTypes.func,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	disabled: PropTypes.bool,
};

export default ToneCard;
